import type { CSSProperties } from "react";

/**
 * Where a popover hangs off the thing that opened it.
 *
 * Below by default, ABOVE when there isn't room below. A menu that runs off the
 * bottom of the window is a menu whose last options cannot be reached at all,
 * and the rows most likely to need it are the ones at the end of a list — which
 * is exactly where a long board puts most of its work.
 *
 * Flipping anchors the panel's BOTTOM edge to the trigger so it grows upward,
 * rather than moving its top and hoping. `maxHeight` then caps it to the room
 * that side actually has, so a tall panel in a short window scrolls inside
 * itself instead of overflowing — the one case neither side can win outright.
 *
 * Deliberately not measured: these panels range from a six-line menu to a
 * textarea, and measuring means rendering once in the wrong place and moving.
 * Choosing the side with more room is right whatever the height turns out to be.
 */
export function anchored(
  rect: DOMRect,
  opts: { width?: number; min?: number; offsetLeft?: number; zIndex?: number } = {},
): CSSProperties {
  const width = opts.width ?? 240;
  // Below this much room, the panel would be a scrolling sliver — prefer the
  // other side even when it is not much bigger.
  const min = opts.min ?? 200;
  const PAD = 10;
  const below = window.innerHeight - rect.bottom - PAD;
  const above = rect.top - PAD;
  const goDown = below >= min || below >= above;
  return {
    position: "fixed",
    left: Math.max(8, Math.min(rect.left + (opts.offsetLeft ?? 0), window.innerWidth - width - 8)),
    maxHeight: Math.max(160, goDown ? below : above),
    overflowY: "auto",
    ...(opts.zIndex ? { zIndex: opts.zIndex } : null),
    ...(goDown ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }),
  };
}
